import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { primaryNav } from "@/mocks/navigation";
import { clinic } from "@/mocks/clinic";
import TreatmentMegaMenu from "./TreatmentMegaMenu";
import ConcernMegaMenu from "./ConcernMegaMenu";
import MobileMenu from "./MobileMenu";
import { ReserveButton } from "./ReserveButton";
import { LineButton } from "./LineButton";

type MenuKey = "treatment" | "concern" | null;

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [activeMenu, setActiveMenu] = useState<MenuKey>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  return (
    <>
      <header
        onMouseLeave={() => setActiveMenu(null)}
        className={`fixed inset-x-0 top-0 z-[70] bg-primary-600 text-background-50 transition-all duration-300 ${
          scrolled ? "shadow-none" : ""
        }`}
      >
        <div
          className={`flex items-center justify-between gap-8 px-6 md:px-10 xl:px-16 transition-all duration-300 ${
            scrolled ? "py-2.5" : "py-5"
          }`}
        >
          <Link
            to="/"
            onMouseEnter={() => setActiveMenu(null)}
            className="flex items-center gap-4 whitespace-nowrap"
          >
            <span
              className={`font-label tracking-[0.22em] transition-all duration-300 ${
                scrolled ? "text-xl" : "text-2xl"
              }`}
            >
              SBDC
            </span>
            <span className="hidden h-6 w-px bg-background-50/25 sm:block" />
            <span className="hidden text-[10px] leading-[1.5] tracking-wider text-background-200 sm:block">
              渋谷美容皮膚科
              <br />
              クリニック
            </span>
          </Link>

          <nav className="hidden flex-1 items-center justify-center gap-8 lg:flex xl:gap-10">
            {primaryNav.map((item) =>
              item.key ? (
                <button
                  key={item.key}
                  type="button"
                  onMouseEnter={() => setActiveMenu(item.key as MenuKey)}
                  onFocus={() => setActiveMenu(item.key as MenuKey)}
                  onClick={() => setActiveMenu((prev) => (prev === item.key ? null : (item.key as MenuKey)))}
                  aria-expanded={activeMenu === item.key}
                  className={`flex items-center gap-1 whitespace-nowrap text-[13px] tracking-[0.06em] transition-colors ${
                    activeMenu === item.key ? "text-background-50" : "text-background-100/85 hover:text-background-50"
                  }`}
                >
                  {item.label}
                  <i
                    className={`ri-arrow-down-s-line text-base transition-transform ${
                      activeMenu === item.key ? "rotate-180" : ""
                    }`}
                    aria-hidden
                  />
                </button>
              ) : (
                <Link
                  key={item.label}
                  to={item.href}
                  onMouseEnter={() => setActiveMenu(null)}
                  className="whitespace-nowrap text-[13px] tracking-[0.06em] text-background-100/85 transition-colors hover:text-background-50"
                >
                  {item.label}
                </Link>
              ),
            )}
          </nav>

          <div className="flex items-center gap-3 md:gap-4">
            <a
              href={clinic.telHref}
              className="hidden items-center gap-1.5 whitespace-nowrap text-[12px] tracking-wide text-background-200/70 transition-colors hover:text-background-100 xl:flex"
            >
              <i className="ri-phone-line text-base" aria-hidden />
              {clinic.tel}
            </a>
            <LineButton className="hidden items-center gap-2 whitespace-nowrap rounded-md border border-background-50/25 px-4 py-2 text-[12.5px] text-background-100 transition-colors hover:border-background-50/45 hover:bg-background-50/10 md:inline-flex">
              <i className="ri-line-fill text-base" aria-hidden />
              LINE相談
            </LineButton>
            <ReserveButton className="hidden items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-5 py-2.5 text-[13px] font-medium tracking-wide text-primary-700 transition-colors hover:bg-secondary-100 md:inline-flex">
              <i className="ri-calendar-check-line text-base" aria-hidden />
              WEB予約
            </ReserveButton>
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              aria-label="メニューを開く"
              className="flex h-10 w-10 items-center justify-center rounded-md border border-background-50/30 text-background-50 lg:hidden"
            >
              <i className="ri-menu-4-line text-xl" aria-hidden />
            </button>
          </div>
        </div>

        {activeMenu === "treatment" ? (
          <div className="absolute inset-x-0 top-full hidden lg:block">
            <TreatmentMegaMenu onNavigate={() => setActiveMenu(null)} />
          </div>
        ) : null}
        {activeMenu === "concern" ? (
          <div className="absolute inset-x-0 top-full hidden lg:block">
            <ConcernMegaMenu onNavigate={() => setActiveMenu(null)} />
          </div>
        ) : null}
      </header>

      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </>
  );
}